import apiClient from '@/api/client';

export interface GrantOut {
  id: string;
  paymaster_company_id: string;
  operated_company_id: string;
  paymaster_company_name: string | null;
  operated_company_name: string | null;
  module: string;
  status: 'pending' | 'active' | 'revoked';
  scope: Record<string, boolean>;
  requires_approval: boolean;
  bank_file_format: 'separate' | 'consolidated';
  cooling_off_seconds: number;
  granted_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  created_at: string;
  updated_at: string;
  is_in_cooloff: boolean;
}

export interface GrantInviteRequest {
  operated_company_short_code?: string;
  operated_company_id?: string;
  scope?: Record<string, boolean>;
  requires_approval?: boolean;
  bank_file_format?: 'separate' | 'consolidated';
  cooling_off_seconds?: number;
  password: string;
}

export interface GrantAcceptRequest {
  password: string;
}

export interface GrantRevokeRequest {
  password: string;
  reason?: string;
}

export interface GrantListResponse {
  items: GrantOut[];
  total: number;
}

export interface GrantListParams {
  status?: 'pending' | 'active' | 'revoked';
  role?: 'paymaster' | 'operated';
  limit?: number;
  offset?: number;
}

export interface IntercompanyPostingOut {
  id: string;
  intercompany_pair_id: string;
  payroll_run_id: string;
  paymaster_company_id: string;
  operated_company_id: string;
  paymaster_journal_id: string | null;
  operated_journal_id: string | null;
  amount: number;
  currency: string;
  status: 'pending' | 'posted' | 'reversed';
  posted_at: string | null;
}

export interface IntercompanyPostResponse {
  payroll_run_id: string;
  paymaster_company_id: string;
  postings: IntercompanyPostingOut[];
  newly_posted_count: number;
  skipped_count: number;
}

export interface OperatedReconciliationLine {
  operated_company_id: string;
  net_pay_total: number;
  posted_amount: number;
  variance: number;
  intercompany_pair_ids: string[];
  posting_status: 'posted' | 'partial' | 'pending';
}

export interface PaymasterReconciliationOut {
  payroll_run_id: string;
  paymaster_company_id: string;
  operated_breakdown: OperatedReconciliationLine[];
  paymaster_bank_out: number;
  total_variance: number;
  is_balanced: boolean;
}

export interface ApprovalOut {
  id: string;
  payroll_run_id: string;
  operated_company_id: string;
  paymaster_company_id: string;
  grant_id: string;
  status: 'pending' | 'approved' | 'rejected';
  approved_by_user_id: string | null;
  approved_at: string | null;
  rejected_by_user_id: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  paymaster_company_name: string | null;
  operated_company_name: string | null;
}

export interface ApprovalListResponse {
  items: ApprovalOut[];
  total: number;
}

export interface ApprovalDecisionRequest {
  decision: 'approve' | 'reject';
  reason?: string;
  password: string;
}

export interface OperatedSummary {
  company_id: string;
  company_name: string;
  company_colour: string | null;
  grant_id: string;
  is_in_cooloff: boolean;
  cool_off_until: string | null;
  requires_approval: boolean;
}

export interface GroupPayrollContextOut {
  company_id: string;
  role: 'standalone' | 'paymaster' | 'operated';
  feature_enabled: boolean;
  paymaster_company_id: string | null;
  paymaster_company_name: string | null;
  paymaster_company_colour: string | null;
  operated_companies: OperatedSummary[];
  pending_invites_count: number;
  pending_approvals_count: number;
  eligibility_hint: boolean;
}

export interface OperatedRunSummary {
  payroll_run_id: string;
  paymaster_company_id: string;
  paymaster_company_name: string | null;
  period_year: number;
  period_month: number;
  period_start: string | null;
  period_end: string | null;
  pay_date: string | null;
  status: string;
  approved_at: string | null;
  employee_count: number;
  operated_net_pay: number;
}

export interface OperatedRunsResponse {
  items: OperatedRunSummary[];
  total: number;
}

export const groupPayrollApi = {
  // Grants
  invite: (body: GrantInviteRequest) =>
    apiClient.post<GrantOut>('/group-payroll/grants', body).then((r) => r.data),
  accept: (grantId: string, body: GrantAcceptRequest) =>
    apiClient
      .post<GrantOut>(`/group-payroll/grants/${grantId}/accept`, body)
      .then((r) => r.data),
  revoke: (grantId: string, body: GrantRevokeRequest) =>
    apiClient
      .post<GrantOut>(`/group-payroll/grants/${grantId}/revoke`, body)
      .then((r) => r.data),
  list: (params?: GrantListParams) =>
    apiClient
      .get<GrantListResponse>('/group-payroll/grants', { params })
      .then((r) => r.data),
  get: (grantId: string) =>
    apiClient.get<GrantOut>(`/group-payroll/grants/${grantId}`).then((r) => r.data),

  // Intercompany posting + reconciliation
  postIntercompanyLegs: (runId: string) =>
    apiClient
      .post<IntercompanyPostResponse>(`/group-payroll/post-payroll/${runId}`)
      .then((r) => r.data),
  reconciliation: (runId: string) =>
    apiClient
      .get<PaymasterReconciliationOut>(`/group-payroll/reconciliation/${runId}`)
      .then((r) => r.data),

  // Sprint 4: context + approval workflow
  context: () =>
    apiClient.get<GroupPayrollContextOut>('/group-payroll/context').then((r) => r.data),
  listRunApprovals: (runId: string) =>
    apiClient
      .get<ApprovalListResponse>(`/group-payroll/runs/${runId}/approvals`)
      .then((r) => r.data),
  pendingApprovalsInbox: (params?: { limit?: number; offset?: number }) =>
    apiClient
      .get<ApprovalListResponse>('/group-payroll/approvals/pending', { params })
      .then((r) => r.data),
  decideApproval: (approvalId: string, body: ApprovalDecisionRequest) =>
    apiClient
      .post<ApprovalOut>(`/group-payroll/approvals/${approvalId}/decide`, body)
      .then((r) => r.data),

  // Operated co's "runs executed on our behalf"
  operatedRuns: (params?: { limit?: number; offset?: number }) =>
    apiClient
      .get<OperatedRunsResponse>('/group-payroll/operated/runs', { params })
      .then((r) => r.data),
};
